import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Gallery } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { checkForNaStringValue, downloadImage } from "utils";

import { type MediaTileItem } from "..";
import { useInspectionsData } from "../hooks";
import { IneligibleWriteOutCard } from "./ineligible-write-out-card";
import styles from "./inspections-ineligible.module.scss";

type IneligibleGalleryData = {
  assetIds: string[];
  title: string;
  subHeading: string;
};

export const InspectionsIneligible = () => {
  const navigate = useNavigate();
  const [galleryData, setGalleryData] = useState<IneligibleGalleryData | null>(
    null,
  );
  const { latestInspection, isLoading: loading } = useInspectionsData();

  const inspectionAssets = latestInspection?.review?.inspectionReviewAssets;
  const statusEnum = latestInspection?.status;

  useEffect(() => {
    if (statusEnum && statusEnum !== 480) {
      navigate("/my/overview");
    }
  }, [navigate, statusEnum]);

  if (loading) {
    return <BaseLayout loading />;
  }

  const referralList = latestInspection?.review?.referrals ?? [];
  const sortedReferralList = referralList.toSorted((referral) => {
    return referral?.category === "Roof" ? -1 : 1;
  });

  const ineligibleReferrals: KeystoneReferral[] = [];
  const otherReferrals: KeystoneReferral[] = [];
  (sortedReferralList ?? []).forEach((referral) => {
    if (referral?.actionStatement === "Risk is ineligible") {
      ineligibleReferrals.push(referral);
    } else if (referral) {
      otherReferrals.push(referral);
    }
  });

  const shownAssets = galleryData?.assetIds;
  if (shownAssets && shownAssets.length > 0) {
    const media: MediaTileItem[] = shownAssets.map((assetId) => {
      const selectedAsset = inspectionAssets?.find(
        (asset) => asset.id === assetId,
      );
      const fileName = selectedAsset?.assetFileName;
      return {
        fileName: fileName ?? `image_${assetId}`,
        id: assetId,
        source: `/api/inspection/image/${assetId}`,
      };
    });

    return (
      <Gallery
        media={media}
        title={galleryData.title}
        subheading={galleryData.subHeading}
        primaryAction="Close"
        onPrimaryAction={() => setGalleryData(null)}
        onClose={() => setGalleryData(null)}
        activeAction="Download"
        showHeroFooter={true}
        onActiveAction={(active: MediaTileItem) => {
          downloadImage(active.source, active.fileName);
        }}
      />
    );
  }

  return (
    <BaseLayout>
      <MainLayout
        topRow={
          <Stack gap="md" className={styles.topRow}>
            <Alert
              appearance="danger"
              description="We may have other carriers that work for your property. Please contact your insurance rep to explore your options."
              title="Your property is ineligible for this policy."
            />
          </Stack>
        }
        sidebar={<InsuranceRepresentative />}
      >
        <Stack gap="xl">
          {ineligibleReferrals.length > 0 ? (
            <Stack gap="xl">
              {ineligibleReferrals.map((referral, idx) => {
                const galleryTitle =
                  checkForNaStringValue(referral?.issueStatement ?? "") ?? "";
                const gallerySubHeading =
                  checkForNaStringValue(referral?.locationDetail ?? "") ?? "";
                return (
                  <IneligibleWriteOutCard
                    key={idx}
                    referral={referral}
                    showGallery={() => {
                      if (referral?.inspectionReviewAssetIds?.length) {
                        setGalleryData({
                          assetIds: referral.inspectionReviewAssetIds ?? [],
                          title: galleryTitle,
                          subHeading: gallerySubHeading,
                        });
                      }
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}

          {otherReferrals.length > 0 ? (
            <Stack gap="xl">
              {otherReferrals.map((referral, idx) => {
                const galleryTitle =
                  checkForNaStringValue(referral?.issueStatement ?? "") ?? "";
                const gallerySubHeading =
                  checkForNaStringValue(referral?.locationDetail ?? "") ?? "";
                return (
                  <IneligibleWriteOutCard
                    key={idx}
                    referral={referral}
                    showGallery={() => {
                      if (referral?.inspectionReviewAssetIds?.length) {
                        setGalleryData({
                          assetIds: referral.inspectionReviewAssetIds ?? [],
                          title: galleryTitle,
                          subHeading: gallerySubHeading,
                        });
                      }
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </MainLayout>
    </BaseLayout>
  );
};
